import { computed, ComputedRef, ref, toRaw } from "vue";
import tinycolor from "tinycolor2";
import lightTheme from "@/themes/_light.json5";
import darkTheme from "@/themes/_dark.json5";
import { useDevicePreferencesStore } from "@/store/device-preferences-store";
import { useSettingsStore } from "@/store/settings-store";
import { storeToRefs } from "pinia";
import { refProp } from "@/composables/reactive";

export type Theme = {
    id: string;
    name: string;
    author: string;
    desc?: string;
    base: "dark" | "light";
    props: Record<string, string>;
};

export const themeProps = Object.keys(lightTheme.props).filter(
    (key) => !key.startsWith("X"),
);

export const getBuiltinThemes = () =>
    Promise.all(
        [
            "l-rosepinedawn",
            "l-light",
            "l-nord",
            "l-gruvbox",
            "l-coffee",
            "l-apricot",
            "l-rainy",
            "l-vivid",
            "l-cherry",
            "l-sushi",
            "l-u0",

            "d-rosepine",
            "d-rosepinemoon",
            "d-dark",
            "d-nord",
            "d-gruvbox",
            "d-catppuccin-frappe",
            "d-catppuccin-mocha",
            "d-persimmon",
            "d-astro",
            "d-future",
            "d-botanical",
            "d-green-lime",
            "d-green-orange",
            "d-cherry",
            "d-ice",
            "d-u0",
        ].map((name) =>
            import(`../themes/${name}.json5`).then(
                ({ default: _default }): Theme => _default,
            ),
        ),
    );

export const getBuiltinThemesRef = () => {
    const builtinThemes = ref<Theme[]>([]);
    getBuiltinThemes().then((themes) => (builtinThemes.value = themes));
    return builtinThemes;
};

export function applyTheme(theme: Theme) {
    const themeCopy = structuredClone(toRaw(theme));

    if (themeCopy.base) {
        const base = [lightTheme, darkTheme].find(
            (x) => x.id === themeCopy.base,
        );
        if (base)
            themeCopy.props = Object.assign({}, base.props, themeCopy.props);
    }

    const props = compile(themeCopy);

    for (const tag of Array.from(document.head.children)) {
        if (
            tag.tagName === "META" &&
            tag.getAttribute("name") === "theme-color"
        ) {
            tag.setAttribute("content", props["htmlThemeColor"]);
            break;
        }
    }

    for (const [k, v] of Object.entries(props)) {
        document.documentElement.style.setProperty(`--${k}`, v.toString());
    }

    const colorScheme = theme.base || theme.id;

    document.documentElement.style.setProperty(
        "--lumaOffset",
        colorScheme === "dark" ? "0.1" : "-0.1",
    );
}

function compile(theme: Theme): Record<string, string> {
    function getColor(val: string): tinycolor.Instance {
        // ref (prop)
        if (val[0] === "@") {
            return getColor(theme.props[val.substring(1)]);
        }

        // ref (const)
        else if (val[0] === "$") {
            return getColor(theme.props[val]);
        }

        // func
        else if (val[0] === ":") {
            const [funcPart, argPart, rest] = val.split("<", 3);
            const func = funcPart.substring(1);
            const arg = parseFloat(argPart);
            const color = getColor(rest);

            switch (func) {
                case "darken":
                    return color.darken(arg);
                case "lighten":
                    return color.lighten(arg);
                case "alpha":
                    return color.setAlpha(arg);
                case "hue":
                    return color.spin(arg);
                case "saturate":
                    return color.saturate(arg);
            }
        }

        // other case
        return tinycolor(val);
    }

    const props = {};

    for (const [k, v] of Object.entries(theme.props)) {
        if (k.startsWith("$")) continue; // ignore const

        props[k] = v.startsWith('"')
            ? v.replace(/^"\s*/, "")
            : genValue(getColor(v));
    }

    return props;
}

function genValue(c: tinycolor.Instance): string {
    return c.toRgbString();
}

export type SchemePreference = {
    mode: "light" | "dark";
};

export function useMagColorScheme(): ComputedRef<SchemePreference> {
    const settingsStore = useSettingsStore();
    const { settings } = storeToRefs(settingsStore);
    const darkMode = refProp(settings, "darkMode");

    return computed(() => ({
        mode: darkMode.value ? "dark" : "light",
    }));
}
